<script lang="ts">
    import Component, { mixins } from 'vue-class-component';
    import PortfolioNavigation from "@common/Components/PortfolioNavigation.vue";
    import moment from "moment";
    import { dateCompare1, generateName, strCompareLowerCase } from '@common/Common';
    import PagesQueries from '@Pages/Domain/queries-vue-component';
    import PagesCommands from '@Pages/Domain/commands-vue-component';
    import { RequestPageReadModel, RequestPagesPageReadModel } from '@Pages/Domain/readmodels';
    import { CloneRequestPageCommandV1, CreateRequestPageCommandV1, DeleteRequestPageCommandV1 } from '@Pages/Domain/commands-core';
    import { ICommandBase } from '@Pages/cqrs';


    @Component({ components: { PortfolioNavigation }  })
    export default class RequestPagesPage extends mixins(PagesQueries, PagesCommands)
    {
        get workspaceId(): number { return Number.parseInt(this.$route.params.workspaceId); }
        get projectId(): number { return Number.parseInt(this.$route.params.projectId); }

        rm: RequestPagesPageReadModel = null;
        SearchPhrase: string = null;
        OrderBy: string = "modified";
        OrderByDescending: boolean = true;

        page: RequestPageReadModel = null;

        get pagesView()
        {
            let view = new Array(...this.rm.Pages);

            if (this.SearchPhrase)
            {
                const phrase = this.SearchPhrase.toLowerCase();
                view = view.filter(o => o.Name.toLowerCase().indexOf(phrase) >= 0);
            }

            if (this.OrderBy === "name")
            {
                view = view.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
            }
            else if (this.OrderBy === "modified")
            {
                view = view.sort((a, b) => dateCompare1(a.Modified, b.Modified));
            }
            else if (this.OrderBy === "created")
            {
                view = view.sort((a, b) => dateCompare1(a.Created, b.Created));
            }

            if (this.OrderByDescending === true)
            {
                view = view.reverse();
            }

            return view;
        }

        getFormattedDateStr(date: string): string | null
        {
            if (date)
            {
                return moment(date).format('MMM D, YYYY');
            }

            return null;
        }

        getFormattedTimeStr(date: string): string | null
        {
            if (date)
            {
                return moment(date).format('LT');
            }

            return null;
        }

        orderByName()
        {
            if (this.OrderBy === 'name')
            {
                this.OrderByDescending = !this.OrderByDescending;
            }
            else
            {
                this.OrderByDescending = false;
                this.OrderBy = 'name';
            }
        }

        orderByModified()
        {
            if (this.OrderBy === 'modified')
            {
                this.OrderByDescending = !this.OrderByDescending;
            }
            else
            {
                this.OrderByDescending = true;
                this.OrderBy = 'modified';
            }
        }

        orderByCreated()
        {
            if (this.OrderBy === 'created')
            {
                this.OrderByDescending = !this.OrderByDescending;
            }
            else
            {
                this.OrderByDescending = true;
                this.OrderBy = 'created';
            }
        }

        CopyUrlCommand()
        {
            navigator.clipboard.writeText(this.page.Url).then(() => 
            {
                const url = this.page.Url;
                this.page.Url = "Copied to clipboard";

                const handle = setTimeout(() =>
                {
                    this.page.Url = url;
                    clearTimeout(handle);
                }, 700);
            }).catch(err => { console.error('Failed to copy: ', err); });
        }

        handleCloneRequestPageCommand(p: RequestPageReadModel)
        {
            this.invokeCloneRequestPageCommandV1(this.workspaceId, this.projectId, p.Id, generateName(p.Name));
        }

        handleDeleteRequestPageCommand(p: RequestPageReadModel)
        {
            if (confirm("Are you sure?") == true)
            {
                this.invokeDeleteRequestPageCommandV1(this.workspaceId, this.projectId, p.Id);
            }
        }

        handleCreateRequestPageCommand()
        {
            this.invokeCreateRequestPageCommandV1(this.workspaceId, this.projectId, 0, generateName("Request page"));
        }

        
        mounted()
        {   
            document.title = "Request pages | TransparentChoice";
            this.runRequestPagesPageQueryV1(this.workspaceId, this.projectId);
        }


        onSetModel(model: any, modelName: string) 
        {
            if (modelName == "RequestPagesPageReadModel")
            {
                this.rm = model as RequestPagesPageReadModel;
            }
        }

        onCommandSucceed(cmd: ICommandBase, correlationId: string, result: any)
        {
            if (CreateRequestPageCommandV1.matchType(cmd))
            {
                const pageId = Number(result);
                this.$router.push(`/pp/edit-page/${this.workspaceId}/${this.projectId}/${pageId}`);
            }
            else if (CloneRequestPageCommandV1.matchType(cmd))
            {
                const pageId = Number(result);
                this.$router.push(`/pp/edit-page/${this.workspaceId}/${this.projectId}/${pageId}`);
            }
            else if (DeleteRequestPageCommandV1.matchType(cmd))
            {
                const pageId = Number(correlationId);
                this.rm.Pages = this.rm.Pages.filter(a => a.Id !== pageId);
            }
        }

        onCommandFailed(status: any)
        {
            if (CreateRequestPageCommandV1.matchType(status.Command))
            {
                for (const error of status.ValidationErrors)
                {
                    alert(error.Error);
                }
            }
            else if (CloneRequestPageCommandV1.matchType(status.Command))
            {
                for (const error of status.ValidationErrors)
                {
                    alert(error.Error);
                }
            }
        }
    }
</script>

<template>
    <div>
        <PortfolioNavigation :workspaceId="workspaceId" :projectId="projectId" />

        <div class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Request Pages</h1>
                </div>
                <div class="col-lg-6 text-right">
                    <button role="button" class="btn btn-success" style="padding: 0.7em 2em;" @click="handleCreateRequestPageCommand">Create page</button>
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 60px;" />

        <div class="container" v-if="rm">
            <div class="row">
                <div class="col-lg-4 offset-lg-8">
                    <div class="input-group input-group-lg">
                        <input type="search" placeholder="Search" class="form-control" v-model="SearchPhrase">
                        <span class="input-group-append">
                            <span class="input-group-text"><i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i></span>
                        </span>
                    </div>
                </div>
            </div>

            <table class="table table-bordered table-hover table-browser mt-3">
                <thead>
                    <tr>
                        <th class="sortable" @click="orderByName()" :class="{ 'selected': OrderBy === 'name' }">
                            <span class="text-uppercase font-weight-normal">Name</span>

                            <span class="fa-stack">
                                <i class="fa fa-sort-asc fa-stack-1x" :class="{ 'selected': OrderBy === 'name' && OrderByDescending === false }"></i>
                                <i class="fa fa-sort-desc fa-stack-1x" :class="{ 'selected': OrderBy === 'name' && OrderByDescending === true }"></i>
                            </span>
                        </th>
                        <th class="sortable text-center" style="width: 180px;" @click="orderByModified()" :class="{ 'selected': OrderBy === 'modified' }">

                            <span class="text-uppercase font-weight-normal">Updated on</span>

                            <span class="fa-stack">
                                <i class="fa fa-sort-asc fa-stack-1x" :class="{ 'selected': OrderBy === 'modified' && OrderByDescending === false }"></i>
                                <i class="fa fa-sort-desc fa-stack-1x" :class="{ 'selected': OrderBy === 'modified' && OrderByDescending === true }"></i>
                            </span>
                        </th>
                        <th class="sortable text-center" style="width: 180px;" @click="orderByCreated()" :class="{ 'selected': OrderBy === 'created' }">

                            <span class="text-uppercase font-weight-normal">Created on</span>

                            <span class="fa-stack">
                                <i class="fa fa-sort-asc fa-stack-1x" :class="{ 'selected': OrderBy === 'created' && OrderByDescending === false }"></i>
                                <i class="fa fa-sort-desc fa-stack-1x" :class="{ 'selected': OrderBy === 'created' && OrderByDescending === true }"></i>
                            </span>
                        </th>
                        <th style="width: 90px;"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="p in pagesView" :key="p.Id">
                        <td>
                            <a class="text-dark" v-sanitizedHref="`/#/pp/edit-page/${workspaceId}/${projectId}/${p.Id}`">{{p.Name}}</a>
                        </td>
                        <td class="text-center">
                            <span>{{getFormattedDateStr(p.Modified)}}</span><br />
                            <small>{{getFormattedTimeStr(p.Modified)}}</small>
                        </td>
                        <td class="text-center">
                            <span>{{getFormattedDateStr(p.Created)}}</span><br />
                            <small>{{getFormattedTimeStr(p.Created)}}</small>
                        </td>
                        <td>
                            <div class="dropdown">
                                <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                    Actions
                                </button>
                                <div class="dropdown-menu dropdown-menu-right">
                                    <a class="dropdown-item" v-sanitizedHref="`/#/pp/edit-page/${workspaceId}/${projectId}/${p.Id}`">Edit</a>
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" v-sanitizedHref="`/#/pageview/${p.AccessKey}`">View</a>
                                    <a class="dropdown-item" v-sanitizedHref="`/#/sharing/resource/PagesDataShareResourceProvider/${p.Id}`">Share</a>
                                    <a class="dropdown-item" @click="handleCloneRequestPageCommand(p)">Clone</a>
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" @click="handleDeleteRequestPageCommand(p)">Delete</a>
                                </div>
                            </div>
                        </td>
                    </tr>

                </tbody>
            </table>
        </div>


    </div>
</template>

<style scoped>

</style>


